export type OwnerOnboardingStep =
  | "basics"
  | "brand"
  | "items"
  | "review"
  | "submitted";

export type OwnerOnboardingStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type OwnerOnboardingResumePath =
  | "/(owner)/onboarding/basics"
  | "/(owner)/onboarding/brand"
  | "/(owner)/onboarding/items"
  | "/(owner)/onboarding/review"
  | "/(owner)/dashboard";

type ResolveOwnerOnboardingResumeInput = {
  status?: unknown;
  /**
   * Retained for call-site compatibility. Business lifecycle data must not use
   * this field to override the owner's saved draft step.
   */
  onboardingStep?: unknown;
  ownerOnboardingStep?: unknown;
};

export type OwnerLifecycleBusiness = {
  id?: string;
  status?: unknown;
  isDeleted?: boolean;
  isPublished?: boolean;
};

export type OwnerOnboardingAccess = "edit" | "review" | "dashboard";

export type OwnerAuthGateState = "loading" | "signin" | "ready" | "error";

export type OwnerOnboardingRouteDecision<T extends OwnerLifecycleBusiness> =
  | { destination: "allow"; business: T }
  | { destination: "dashboard"; business: T | null }
  | { destination: "start"; business: null };

export type OwnerDashboardLifecycle = {
  canEdit: boolean;
  canViewSubmitted: boolean;
  canWithdraw: boolean;
  canManageApproved: boolean;
  canViewPublic: boolean;
  isAwaitingPublication: boolean;
};

function normalizeStep(value: unknown): OwnerOnboardingStep | null {
  return value === "basics" ||
    value === "brand" ||
    value === "items" ||
    value === "review" ||
    value === "submitted"
    ? value
    : null;
}

export function normalizeOwnerOnboardingStatus(
  value: unknown
): OwnerOnboardingStatus | null {
  return value === "draft" ||
    value === "pending" ||
    value === "approved" ||
    value === "rejected" ||
    value === "changes_requested"
    ? value
    : null;
}

export function resolveOwnerAuthGate(input: {
  authInitialized: boolean;
  hasUser: boolean;
  ownerReady: boolean;
  ownerError: boolean;
}): OwnerAuthGateState {
  if (!input.authInitialized) return "loading";
  if (!input.hasUser) return "signin";
  if (input.ownerError) return "error";
  if (!input.ownerReady) return "loading";
  return "ready";
}

function lifecycleSelectionRank(business: OwnerLifecycleBusiness): number {
  const status = normalizeOwnerOnboardingStatus(business.status);

  // Unrecognized active records fail closed and must never lose to a draft.
  if (!status) return 5;
  if (status === "pending" || status === "approved") return 4;
  if (status === "rejected" || status === "changes_requested") return 3;
  return 2;
}

/**
 * Selects the authoritative active owner business. Missing isDeleted is a
 * valid legacy record; only an explicit true is treated as deleted.
 */
export function selectOwnerLifecycleBusiness<
  T extends OwnerLifecycleBusiness,
>(businesses: T[], requestedBusinessId?: string | null): T | null {
  const active = businesses.filter((business) => business.isDeleted !== true);
  if (active.length === 0) return null;

  const highestRank = Math.max(...active.map(lifecycleSelectionRank));
  const candidates = active.filter(
    (business) => lifecycleSelectionRank(business) === highestRank
  );
  const requested = requestedBusinessId
    ? candidates.find((business) => business.id === requestedBusinessId)
    : null;

  return requested ?? candidates[0] ?? null;
}

export function resolveOwnerOnboardingRouteDecision<
  T extends OwnerLifecycleBusiness,
>(input: {
  businesses: T[];
  requestedBusinessId?: string | null;
  approvedEditMode?: boolean;
  correctionAuthorized?: boolean;
}): OwnerOnboardingRouteDecision<T> {
  const explicitlyRequested = input.requestedBusinessId
    ? input.businesses.find(
        (business) => business.id === input.requestedBusinessId
      ) ?? null
    : null;

  if (explicitlyRequested?.isDeleted === true) {
    return { destination: "dashboard", business: explicitlyRequested };
  }

  const selected = selectOwnerLifecycleBusiness(
    input.businesses,
    input.requestedBusinessId
  );
  if (!selected) return { destination: "start", business: null };

  const access = resolveOwnerOnboardingAccess({
    status: selected.status,
    isDeleted: selected.isDeleted,
    isPublished: selected.isPublished,
    approvedEditMode: input.approvedEditMode,
    correctionAuthorized: input.correctionAuthorized,
  });

  return access === "edit"
    ? { destination: "allow", business: selected }
    : { destination: "dashboard", business: selected };
}

/**
 * Owner entry is lifecycle-first. Any existing non-draft (or unrecognized)
 * record belongs on the dashboard; only an all-draft set may enter /start.
 */
export function resolveOwnerEntryPath(
  businesses: OwnerLifecycleBusiness[]
): "/(owner)/start" | "/(owner)/dashboard" {
  const activeBusinesses = businesses.filter(
    (business) => business.isDeleted !== true
  );

  if (activeBusinesses.length === 0) return "/(owner)/start";

  return activeBusinesses.every(
    (business) => normalizeOwnerOnboardingStatus(business.status) === "draft"
  )
    ? "/(owner)/start"
    : "/(owner)/dashboard";
}

export function resolveOwnerOnboardingAccess(input: {
  status?: unknown;
  isPublished?: boolean;
  isDeleted?: boolean;
  approvedEditMode?: boolean;
  correctionAuthorized?: boolean;
}): OwnerOnboardingAccess {
  if (input.isDeleted === true) return "dashboard";

  const status = normalizeOwnerOnboardingStatus(input.status);

  if (status === "pending") return "dashboard";

  if (status === "draft") {
    return input.approvedEditMode ? "dashboard" : "edit";
  }

  if (status === "rejected" || status === "changes_requested") {
    return !input.approvedEditMode && input.correctionAuthorized === true
      ? "edit"
      : "dashboard";
  }

  if (
    status === "approved" &&
    input.isPublished === true &&
    input.approvedEditMode === true
  ) {
    return "edit";
  }

  return "dashboard";
}

export function resolveOwnerDashboardLifecycle(
  business: OwnerLifecycleBusiness
): OwnerDashboardLifecycle {
  const status = normalizeOwnerOnboardingStatus(business.status);
  const isAvailable = business.isDeleted !== true;
  const canEdit =
    isAvailable &&
    (status === "draft" ||
      status === "rejected" ||
      status === "changes_requested");
  const canManageApproved =
    isAvailable && status === "approved" && business.isPublished === true;

  return {
    canEdit,
    canViewSubmitted: isAvailable && status === "pending",
    canWithdraw: isAvailable && status === "pending",
    canManageApproved,
    canViewPublic: canManageApproved,
    isAwaitingPublication:
      isAvailable && status === "approved" && business.isPublished !== true,
  };
}

/**
 * Resolves every owner onboarding entry point through the same lifecycle-safe
 * status and saved-step rules.
 */
export function resolveOwnerOnboardingResumePath({
  status,
  ownerOnboardingStep,
}: ResolveOwnerOnboardingResumeInput): OwnerOnboardingResumePath {
  // Lifecycle always wins. Rejected/change-requested records begin corrections
  // from an explicit dashboard action, never from a saved onboarding step.
  if (normalizeOwnerOnboardingStatus(status) !== "draft") {
    return "/(owner)/dashboard";
  }

  switch (normalizeStep(ownerOnboardingStep) ?? "basics") {
    case "brand":
      return "/(owner)/onboarding/brand";
    case "items":
      return "/(owner)/onboarding/items";
    case "review":
    case "submitted":
      return "/(owner)/onboarding/review";
    case "basics":
    default:
      return "/(owner)/onboarding/basics";
  }
}
